/* eslint-disable @typescript-eslint/naming-convention */
import {
  push,
  createMatchSelector,
} from 'connected-react-router';
import {
  select,
  put,
  race,
  take,
  takeEvery,
  call,
} from 'redux-saga/effects';
import {
  CheckTokenJsonApiPayload,
  CheckTokenJsonApiErrors,
} from '../../../../@types/jsonapi.d';
import { SignupFlow } from '../../../../@types/signup.d';
import ApiResource from '../../../../api-resources';
import { setContainerLoadState } from '../../../../actions/container-load-state';
import {
  SIGNUP_WITH_VANILLA,
  createUserFromVanilla,
  SIGNUP_WITH_LINKEDIN,
  getUserFromLinkedin,
  createUserFromLinkedin,
  checkSignupToken,
  getEmployeeFromInvitation,
  getCompanyAdminFromInvitation,
  checkValidityOfSchoolToken,
} from '../actions/signup';
import { populateSignupForm } from '../actions/signup-form-data';
import { displayToastr } from '../../../../actions/toastr';
import Routes from '../../../../routes';
import { urlSearchSelector } from '../../../../selectors/router';
import {
  createErrorActionType,
  createSuccessActionType,
} from '../../../../utils/actions';
import {
  extractFirstJsonapiResourceFromAction,
  extractErrorsFromPayload,
} from '../../../../utils/json-api';
import { scrollToTopOfPage } from '../../../../utils/routes';
import { translateErrorCode } from '../../../../utils/translate';
import { studentSignupFlow } from './signup-saga/flows/students';
import { professionalSignupFlow } from './signup-saga/flows/professionals';
import { companyAdminSignupFlow } from './signup-saga/flows/companyAdmin';
import { schoolCoachSignupFlow } from './signup-saga/flows/schoolCoach';
import translations from './signup.translations';

function* retrieveSignupCodes() {
  const {
    sign_up_token,
    invitation_token,
    school_token,
  } = yield select(urlSearchSelector);
  return {
    sign_up_token,
    invitation_token,
    school_token,
  };
}

type FlowSaga = {
  [key in keyof typeof SignupFlow]: (...params: any[]) => any;
};
interface RegistrationFlowParams {
  flow: SignupFlow;
  authCredentials: string | object;
}
function* registrationFlow({
  flow,
  authCredentials = 'students',
}: RegistrationFlowParams) {
  const flows: FlowSaga = {
    [SignupFlow.students]: studentSignupFlow,
    [SignupFlow.mentors]: professionalSignupFlow,
    [SignupFlow.ambassador]: professionalSignupFlow,
    [SignupFlow.employee]: professionalSignupFlow,
    [SignupFlow.company_admins]: companyAdminSignupFlow,
    [SignupFlow['school-coach']]: schoolCoachSignupFlow,
  };
  const flowSaga = flows[flow];
  if (!flowSaga) {
    throw new Error(`Unknown flow ${flow}`);
  }
  yield call(flowSaga, { flow, authCredentials });
}

interface HandleVanillaSignupParams {
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  company_name: string,
  flow: SignupFlow,
  resolve: PromiseResolveFn,
  reject: PromiseRejectFn,
}
function* handleVanillaSignup({
  firstName,
  lastName,
  email,
  password,
  company_name,
  flow,
  resolve,
  reject,
}: HandleVanillaSignupParams) {
  yield put(setContainerLoadState('signup-view', { sending: true }));
  const {
    sign_up_token,
    invitation_token,
    school_token,
  } = yield call(retrieveSignupCodes);

  yield put(createUserFromVanilla({
    firstName,
    lastName,
    email,
    password,
    company_name,
    sign_up_token,
    invitation_token,
    school_token,
    flow,
  }));
  const { error } = yield race({
    success: take(createSuccessActionType('CREATE', ApiResource.USER)),
    error: take(createErrorActionType('CREATE', ApiResource.USER)),
  });

  if (error) {
    yield put(setContainerLoadState('signup-view', { error: error.error }));
    if (typeof reject === 'function') {
      reject(error.error);
    }
    return;
  }

  yield call(registrationFlow, {
    flow,
    authCredentials: {
      firstName,
      lastName,
      email,
      password,
    },
  });

  if (typeof resolve === 'function') {
    resolve();
  }

  yield put(setContainerLoadState('signup-view', { success: true }));
  scrollToTopOfPage();
}

interface HandleLinkedinSignupParams {
  code: string;
  flow: SignupFlow;
  resolve: PromiseResolveFn;
  reject: PromiseRejectFn;
}
function* handleLinkedinSignup({
  code,
  flow,
  resolve,
  reject,
}: HandleLinkedinSignupParams) {
  yield put(setContainerLoadState('signup-view', { sending: true }));
  yield put(getUserFromLinkedin({ code })); // to know if user exist
  const { identitySuccess, identityError } = yield race({
    identitySuccess: take(createSuccessActionType('CREATE', ApiResource.IDENTITY)),
    identityError: take(createErrorActionType('CREATE', ApiResource.IDENTITY)),
  });
  if (identityError) {
    const error = extractErrorsFromPayload(identityError)[0];
    yield put(setContainerLoadState('signup-view', { error }));
    if (typeof reject === 'function') {
      reject(error);
    }
    return;
  }

  const {
    sign_up_token,
    invitation_token,
  } = yield call(retrieveSignupCodes);
  const identity = extractFirstJsonapiResourceFromAction(identitySuccess, 'identity');
  yield put(createUserFromLinkedin({
    identity_id: identity.id,
    sign_up_token,
    invitation_token,
    flow,
  }));
  const { userError } = yield race({
    userSuccess: take(createSuccessActionType('CREATE', ApiResource.USER)),
    userError: take(createErrorActionType('CREATE', ApiResource.USER)),
  });

  if (userError) {
    const error = extractErrorsFromPayload(userError)[0];
    yield put(setContainerLoadState('signup-view', { error }));
    if (typeof reject === 'function') {
      reject(error);
    }
    return;
  }
  yield call(registrationFlow, {
    flow,
    authCredentials: {
      flavor: 'identity',
      email: identity.attributes.oauthInfo.email,
      identity_id: identity.id,
    },
  });
  if (typeof resolve === 'function') {
    resolve();
  }
  yield put(setContainerLoadState('signup-view', { success: true }));
  scrollToTopOfPage();
}

interface HandleCheckSignupTokenParams {
  token: string;
}
function* handleCheckSignupToken({ token }: HandleCheckSignupTokenParams) {
  yield put(setContainerLoadState('signup-view', { loading: true }));
  yield put(checkSignupToken({ token }));
  const { success, error } = yield race({
    success: take(createSuccessActionType('READ', ApiResource.MENTOR_ACQUISITION_SIGNUP_TOKEN)),
    error: take(createErrorActionType('READ', ApiResource.MENTOR_ACQUISITION_SIGNUP_TOKEN)),
  });
  if (error) {
    const errors = extractErrorsFromPayload(error);
    const errorCode = errors?.[0]?.name;
    yield put(displayToastr(
      'error',
      translateErrorCode<keyof typeof CheckTokenJsonApiErrors>(
        translations,
        errorCode,
        undefined,
        true,
      ),
      undefined,
      { timeOut: 0 },
    ));
    return;
  }
  const formData = Object.values<CheckTokenJsonApiPayload>(
    success?.data?.mentorAcquisitionContactRequest || {},
  )[0] || {};
  if (formData) {
    yield put(populateSignupForm({
      email: formData?.attributes?.email,
      firstName: formData?.attributes?.firstName,
      lastName: formData?.attributes?.lastName,
      sign_up_token: formData?.attributes?.signUpToken,
    }));
  }

  yield put(setContainerLoadState('signup-view', { loading: false }));
}

interface HandleCheckEmployeeInvitationTokenParams {
  token: string;
}
function* handleCheckEmployeeInvitationToken({ token }: HandleCheckEmployeeInvitationTokenParams) {
  yield put(setContainerLoadState('signup-view', { loading: true }));
  yield put(getEmployeeFromInvitation({ token }));
  const { success, error } = yield race({
    success: take(createSuccessActionType('READ', ApiResource.PROFILE_EMPLOYEE)),
    error: take(createErrorActionType('READ', ApiResource.PROFILE_EMPLOYEE)),
  });

  if (error) {
    const errors = extractErrorsFromPayload(error);
    const errorCode = errors?.[0]?.name;
    yield put(push(Routes.signinView, { signUpTokenError: errorCode }));
    return;
  }
  const formData = Object.values<CheckTokenJsonApiPayload>(success?.data?.profileEmployee || {})[0];
  if (formData) {
    yield put(populateSignupForm({
      email: formData?.attributes?.email,
      firstName: formData?.attributes?.firstName,
      lastName: formData?.attributes?.lastName,
      invitation_token: formData?.attributes?.invitationToken,
    }));
  }
  yield put(setContainerLoadState('signup-view', { loading: false }));
}

interface HandleCheckCompanyAdminInvitationTokenParams {
  token: string;
}
function* handleCheckCompanyAdminInvitationToken({
  token,
}: HandleCheckCompanyAdminInvitationTokenParams) {
  yield put(setContainerLoadState('signup-view', { loading: true }));
  yield put(getCompanyAdminFromInvitation({ token }));
  const { success, error } = yield race({
    success: take(createSuccessActionType('READ', ApiResource.PROFILE_COMPANY_ADMIN)),
    error: take(createErrorActionType('READ', ApiResource.PROFILE_COMPANY_ADMIN)),
  });

  if (error) {
    const errors = extractErrorsFromPayload(error);
    const errorCode = errors?.[0]?.name;
    yield put(push(Routes.signinView, { signUpTokenError: errorCode }));
    return;
  }

  const formData = Object.values<CheckTokenJsonApiPayload>(
    success?.data?.profileCompanyAdminInvitation || {},
  )[0];
  if (formData) {
    yield put(populateSignupForm({
      firstName: formData?.attributes?.firstName,
      email: formData?.attributes?.email,
      lastName: formData?.attributes?.lastName,
      invitation_token: formData?.attributes?.invitationToken,
    }));
  }
  yield put(setContainerLoadState('signup-view', { loading: false }));
}

interface HandleCheckSchoolCoachTokenParams {
  token: string;
}
function* handleCheckSchoolCoachToken({ token }: HandleCheckSchoolCoachTokenParams) {
  yield put(setContainerLoadState('signup-view', { loading: true }));
  yield put(checkValidityOfSchoolToken({ token }));
  const { error } = yield race({
    success: take(createSuccessActionType('HEAD', ApiResource.SCHOOL_TOKEN)),
    error: take(createErrorActionType('HEAD', ApiResource.SCHOOL_TOKEN)),
  });

  if (error) {
    const errors = extractErrorsFromPayload(error);
    const errorCode = errors?.[0]?.name;
    yield put(push(Routes.signinView, { signUpTokenError: errorCode }));
    return;
  }

  yield put(setContainerLoadState('signup-view', { loading: false }));
}

export function* hydrateSignupView() {
  const { sign_up_token, invitation_token, school_token } = yield select(urlSearchSelector);
  const match = yield select(createMatchSelector(Routes.signupView));
  if (sign_up_token) {
    yield call(handleCheckSignupToken, { token: sign_up_token });
  } else if (invitation_token && match?.params?.flow === SignupFlow.ambassador) {
    yield call(handleCheckEmployeeInvitationToken, { token: invitation_token });
  } else if (invitation_token && match?.params?.flow === SignupFlow.company_admins) {
    yield call(handleCheckCompanyAdminInvitationToken, { token: invitation_token });
  } else if (school_token) {
    yield call(handleCheckSchoolCoachToken, { token: school_token });
  }
  // @ts-ignore
  yield takeEvery(SIGNUP_WITH_VANILLA, handleVanillaSignup);
  // @ts-ignore
  yield takeEvery(SIGNUP_WITH_LINKEDIN, handleLinkedinSignup);
}
